import { useEffect, useMemo, useRef, useState } from "react";

import { Conversation } from "@/pages/Private/pages/Apollo/schema/get-people";

import {
	useGetSendersUsersQuery,
	useLazyCheckLinkedinConnectionQuery,
} from "@/pages/Private/redux/admin/admin.api";

import {
	getUserIdsWithLinkedinConversation,
	LinkedinSenderStates,
	NO_SENDER_STATES,
	senderStatesForFailedCheck,
	senderStatesFromCheck,
} from "./filterLinkedinSenders.util";
import { isLastSenderConnectedTo, resolveLastSenderUserId } from "./lastSender.util";

interface UseProspectLinkedinConnectionsParams {
	/** The prospect's LinkedIn profile URL — the identity the connection check runs against. */
	prospectLinkedinUrl?: string | null;
	/** The prospect's conversations, used to resolve the last sender + existing-conversation senders. */
	conversations?: Conversation[] | null;
	/** The list_person the sequence is for (a prospect can be on several lists). */
	listPersonId?: number;
}

/**
 * Resolves which sending users are already connected to a prospect on LinkedIn (or hold a chat
 * with them), whose own invitation is still pending (PREC-4216),
 * plus the last sender and whether that sender is connected — the wiring the
 * {@link GenerateBar} needs to auto-disable the "Add note to LinkedIn invite"
 * toggle for a connected sender.
 *
 * Extracted from `useAddInboxChat` so the Feed / reach-out entry points
 * (`ReachOutAiPanel`) share the exact same detection instead of defaulting the
 * invite-note toggle on regardless of connection status.
 */
export const useProspectLinkedinConnections = ({
	prospectLinkedinUrl,
	conversations,
	listPersonId,
}: UseProspectLinkedinConnectionsParams) => {
	const { data: sendingUsers } = useGetSendersUsersQuery();
	const [checkConnection] = useLazyCheckLinkedinConnectionQuery();
	const [senderStates, setSenderStates] = useState<LinkedinSenderStates>(NO_SENDER_STATES);
	const [connectionCheckDone, setConnectionCheckDone] = useState(false);
	// Every check takes a number; an answer to a superseded one (an older prospect) is dropped, so it
	// cannot set this prospect's connections or "done" (the PREC-4222 race, PREC-4312).
	const requestSeq = useRef(0);

	useEffect(() => {
		const seq = ++requestSeq.current;
		const isCurrent = () => seq === requestSeq.current;

		// Senders not loaded yet: nothing is known. Reporting "done, nobody connected" here let the
		// GenerateBar settle on a channel before the real answer (PREC-4312).
		if (prospectLinkedinUrl && !sendingUsers?.data) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setConnectionCheckDone(false);

			return;
		}

		if (!prospectLinkedinUrl || !sendingUsers?.data) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSenderStates(NO_SENDER_STATES);
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setConnectionCheckDone(true);

			return;
		}

		const linkedinUserIds = sendingUsers.data.filter((u) => u.unipile?.isLoggedIn).map((u) => u.id);

		if (linkedinUserIds.length === 0) {
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setSenderStates(NO_SENDER_STATES);
			// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
			setConnectionCheckDone(true);

			return;
		}

		// Users that already hold a LinkedIn chat with this lead (never an invitation-only row).
		const usersWithChat = getUserIdsWithLinkedinConversation(
			conversations ?? [],
			listPersonId ?? 0
		);

		// eslint-disable-next-line @eslint-react/hooks-extra/no-direct-set-state-in-use-effect
		setConnectionCheckDone(false);
		checkConnection({ userIds: linkedinUserIds, linkedinUrl: prospectLinkedinUrl, listPersonId })
			.unwrap()
			.then((response) => {
				if (isCurrent()) {
					setSenderStates(senderStatesFromCheck(response.results, usersWithChat));
				}
			})
			.catch(() => {
				// Fail closed: a failed check is not "connected" and not "no invitation" (PREC-4216).
				if (isCurrent()) {
					setSenderStates(senderStatesForFailedCheck(linkedinUserIds));
				}
			})
			.finally(() => {
				if (isCurrent()) {
					setConnectionCheckDone(true);
				}
			});
	}, [prospectLinkedinUrl, sendingUsers?.data, conversations, listPersonId, checkConnection]);

	const lastSenderUserId = useMemo(() => resolveLastSenderUserId(conversations), [conversations]);
	const { connectedUserIds } = senderStates;
	const pendingInvitationUserIds = useMemo(
		() => new Set(senderStates.pendingInvitations.keys()),
		[senderStates]
	);
	const isLastSenderConnected = isLastSenderConnectedTo(lastSenderUserId, connectedUserIds);

	return {
		connectedUserIds,
		/** Senders whose own invitation to the lead is pending (PREC-4216). */
		pendingInvitationUserIds,
		connectionCheckDone,
		lastSenderUserId,
		isLastSenderConnected,
	};
};
